import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { useSearchParams } from 'react-router-dom';
import { FaCheckCircle, FaChevronDown, FaDownload, FaPlus, FaSearch, FaTrash, FaUndo } from 'react-icons/fa';
import dirStyles from './DirectoryPage.module.css';
import styles from './VouchersWorkspacePage.module.css';
import VouchersWorkspaceSidebar from './vouchers/VouchersWorkspaceSidebar';
import { Badge, Button, Card, CardBody, Input, LabelChip, Pagination } from '../shared/ui';
import {
  PAGE_SIZE_DEFAULT,
  PAGE_SIZE_MAX,
  PAGE_SIZE_MIN,
  setTablePageSizePreference,
  useTablePageSizePreference
} from '../shared/ui/tablePageSize';
import VoucherImportModal from '../components/VoucherImportModal';
import VoucherStudentWorkspaceModal from '../components/VoucherStudentWorkspaceModal';
import Modal from '../components/Modal';
import { getVouchers, deleteVouchersInBatches } from '../services/voucherService';
import { PopupContext } from '../App';
import { API_URL } from '../config/env';
import { getVoucherStatuses } from '../services/voucherStatusService';
import { getVoucherStatusNotesCounts } from '../services/voucherStatusNotesCountsService';
import { setClosureStatus, getAllClosureStatuses } from '../services/voucherStatusClosureService';
import { getSettlementDatesForWorkspace, getSettlementDatesWithVouchers } from '../services/settlementDateService';
import { getLocations } from '../services/locationService';
import { listLabels } from '../services/labels';
import { VOUCHER_IMPORT_HEADERS } from '../constants/voucherImportColumns';

function formatEnUsNumericDate(value) {
  if (!value) return '';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'numeric', day: 'numeric' });
}

/** Decimal cells in the same style as import / sample (e.g. 698.70). */
function formatImportDecimalCell(v) {
  if (v == null || v === '') return '';
  const n = Number(v);
  if (Number.isNaN(n)) return '';
  return n.toFixed(2);
}

/** "LASTNAME , FIRST" in ALL CAPS when built from directory name. */
function formatChildNameLastCommaFirst(name) {
  if (!name || typeof name !== 'string') return '';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0].toUpperCase();
  const last = parts[parts.length - 1].toUpperCase();
  const first = parts.slice(0, -1).join(' ').toUpperCase();
  return `${last} , ${first}`;
}

function childNameForSimpleExport(studentName, voucherChildName) {
  const raw = String(voucherChildName || '').trim();
  if (raw) return raw;
  return formatChildNameLastCommaFirst(studentName);
}

function voucherToImportExportRow(p) {
  return {
    child_name: p.child_name != null ? String(p.child_name) : '',
    case_number: p.case_number != null ? String(p.case_number) : '',
    agreement_number: p.agreement_number != null ? String(p.agreement_number) : '',
    eligibility_type: p.eligibility_type != null ? String(p.eligibility_type) : '',
    aa: formatImportDecimalCell(p.aa),
    sda: formatImportDecimalCell(p.sda),
    ada: formatImportDecimalCell(p.ada),
    cda: formatImportDecimalCell(p.cda),
    total: formatImportDecimalCell(p.total)
  };
}

function buildImportShapeExportBuffer(dataRows) {
  const headers = VOUCHER_IMPORT_HEADERS;
  const aoa = [headers, ...dataRows.map((obj) => headers.map((h) => obj[h] ?? ''))];
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Vouchers');
  return XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
}

/** Must match roster `location_address` (voucher-status uses location `address_line1` only). */
function locationLabelForVoucherRow(v, locations) {
  if (v.location_id == null || v.location_id === '') return 'No location';
  const loc = locations.find((l) => String(l.id) === String(v.location_id));
  const addr = loc?.address_line1;
  if (addr != null && String(addr).trim() !== '') return String(addr).trim();
  return 'No location';
}

/** Voucher row ids attached to the current filtered roster (from voucher-status `payments`). */
function voucherIdsFromFilteredRoster(filteredData) {
  const ids = new Set();
  for (const r of filteredData) {
    for (const p of Array.isArray(r.payments) ? r.payments : []) {
      if (p?.id != null) ids.add(String(p.id));
    }
  }
  return ids;
}

/** Students who already have at least one voucher row in `list` (by PK or by payment id). */
function studentPksCoveredByVoucherList(list, filteredData) {
  const covered = new Set();
  const listIds = new Set(list.map((v) => (v?.id != null ? String(v.id) : null)).filter(Boolean));
  const sid = (x) => (x != null ? String(x) : '');
  for (const v of list) {
    const k = sid(v.student_pk_id);
    if (k) covered.add(k);
  }
  for (const row of filteredData) {
    const pays = Array.isArray(row.payments) ? row.payments : [];
    if (pays.some((p) => p?.id != null && listIds.has(String(p.id)))) {
      covered.add(sid(row.student_id));
    }
  }
  return covered;
}

/** Narrow API voucher list to match workspace location + roster filters (when a roster slice is active). */
function filterVoucherApiListForWorkspace(list, locations, locationFilter, filteredData) {
  let out = Array.isArray(list) ? [...list] : [];
  if (locationFilter !== 'all') {
    out = out.filter((v) => locationLabelForVoucherRow(v, locations) === locationFilter);
  }
  if (filteredData.length > 0) {
    const allowedPk = new Set(filteredData.map((r) => String(r.student_id)));
    const allowedVoucherIds = voucherIdsFromFilteredRoster(filteredData);
    out = out.filter((v) => {
      const pkOk = v.student_pk_id != null && allowedPk.has(String(v.student_pk_id));
      const idOk = v.id != null && allowedVoucherIds.has(String(v.id));
      return pkOk || idOk;
    });
  }
  return out;
}

function emptyImportShapeRowFromRoster(row) {
  return {
    child_name: childNameForSimpleExport(row.name, null),
    case_number: '',
    agreement_number: '',
    eligibility_type: '',
    aa: '',
    sda: '',
    ada: '',
    cda: '',
    total: ''
  };
}

/** @param {object} v – row from GET /api/vouchers (joined student fields optional) */
function voucherApiToSimpleExportRow(v, periodDateDisplay) {
  const dirName = [v.student_first_name, v.student_last_name].filter(Boolean).join(' ').trim();
  return {
    'Child Name': childNameForSimpleExport(dirName || '', v.child_name),
    VoucherDate: formatEnUsNumericDate(v.created_at) || periodDateDisplay,
    Received: formatReceivedDollarExport(v.total)
  };
}

/** Compact export — legacy 3 columns (not import shape). */
const SIMPLE_VOUCHER_EXPORT_HEADERS = ['Child Name', 'VoucherDate', 'Received'];

function formatReceivedDollarExport(n) {
  const x = Number(n);
  if (Number.isNaN(x)) return '$0.00';
  return `$${x.toFixed(2)}`;
}

function buildSimpleVoucherExportBuffer(dataRows) {
  const headers = SIMPLE_VOUCHER_EXPORT_HEADERS;
  const aoa = [headers, ...dataRows.map((obj) => headers.map((h) => obj[h] ?? ''))];
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Export');
  return XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
}

const ROW_MEASURE_DEBOUNCE_MS = 120;
const FALLBACK_THEAD_H = 32;
const FALLBACK_ROW_H = 34;

function computeFitRowsForContainer(containerEl) {
  if (!containerEl || containerEl.clientHeight < FALLBACK_THEAD_H + FALLBACK_ROW_H) return null;
  const table = containerEl.querySelector('table');
  const thead = table?.querySelector('thead');
  const row = table?.querySelector('tbody tr');
  const theadH = thead?.offsetHeight ?? FALLBACK_THEAD_H;
  const rowRect = row?.getBoundingClientRect();
  const rowH = rowRect && rowRect.height >= 12 ? rowRect.height : FALLBACK_ROW_H;
  const avail = containerEl.clientHeight - theadH - 8;
  if (avail <= 0) return null;
  return Math.max(PAGE_SIZE_MIN, Math.min(PAGE_SIZE_MAX, Math.floor(avail / rowH)));
}

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('token')}` };
}

/** Key for per-student maps; voucher-status and /api/students may use number vs string ids. */
function studentRowKey(studentId) {
  if (studentId == null || studentId === '') return '';
  return String(studentId);
}

function matchesPreset(row, preset) {
  if (preset === 'all') return true;
  if (preset === 'attention') return ['missing', 'partial', 'overpaid'].includes(row.status);
  return row.status === preset;
}

function parseLabelIdsParam(raw) {
  if (!raw) return [];
  return raw
    .split(',')
    .map((v) => parseInt(v.trim(), 10))
    .filter((n) => !Number.isNaN(n) && n > 0);
}

/** Student must have at least one of the selected labels (directory-style OR). */
function rowMatchesLabelFilter(row, labelFilterIds, studentLabels) {
  if (!labelFilterIds.length) return true;
  const k = studentRowKey(row.student_id);
  const ids = new Set((studentLabels[k] || []).map((l) => l.id));
  return labelFilterIds.some((id) => ids.has(id));
}

function rowMatchesSearch(row, searchTerm, studentLabels) {
  if (!searchTerm.trim()) return true;
  const term = searchTerm.trim().toLowerCase();
  const name = row.name ? row.name.toLowerCase() : '';
  const family = row.family_name ? row.family_name.toLowerCase() : '';
  const full1 = `${name} ${family}`.trim();
  const full2 = `${family} ${name}`.trim();
  const lbls = studentLabels[studentRowKey(row.student_id)] || [];
  const labelHay = lbls.map((l) => (l.name || '').toLowerCase()).join(' ');
  return (
    name.includes(term) ||
    family.includes(term) ||
    full1.includes(term) ||
    full2.includes(term) ||
    labelHay.includes(term)
  );
}

function badgeVariant(status) {
  switch (status) {
    case 'complete':
      return 'success';
    case 'partial':
      return 'warning';
    case 'missing':
      return 'danger';
    case 'overpaid':
      return 'info';
    case 'closed':
      return 'neutral';
    default:
      return 'neutral';
  }
}

function formatMoney(n) {
  const x = Number(n);
  if (Number.isNaN(x)) return '—';
  return `$${x.toFixed(2)}`;
}

/** Single-line label for a settlement_dates row (matches sidebar import label). */
function settlementPeriodLabel(d) {
  if (!d?.settlement_date) return d?.date_range || '—';
  const [datePart] = String(d.settlement_date).split('T');
  const [y, m, day] = datePart.split('-');
  const dt = new Date(Number(y), Number(m) - 1, Number(day));
  return Number.isNaN(dt.getTime())
    ? String(d.date_range || '')
    : dt.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export default function VouchersWorkspacePage() {
  const showPopup = useContext(PopupContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const pageSizePref = useTablePageSizePreference();
  const urlPageSize = Math.min(
    PAGE_SIZE_MAX,
    Math.max(PAGE_SIZE_MIN, parseInt(searchParams.get('size') || String(PAGE_SIZE_DEFAULT), 10) || PAGE_SIZE_DEFAULT)
  );
  const pageSize = pageSizePref.mode === 'manual' ? pageSizePref.size : urlPageSize;
  const page = Math.max(1, parseInt(searchParams.get('page') || '1', 10));

  const [settlementDates, setSettlementDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [statusData, setStatusData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [preset, setPreset] = useState(() => new URLSearchParams(window.location.search).get('view') || 'all');
  const [locationFilter, setLocationFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState(() => new URLSearchParams(window.location.search).get('search') || '');

  const [sortConfig, setSortConfig] = useState({ key: 'gap', direction: 'desc' });
  const [closureLoading, setClosureLoading] = useState({});

  const [detailStudentId, setDetailStudentId] = useState(null);
  const [notesCounts, setNotesCounts] = useState({});

  const [importOpen, setImportOpen] = useState(false);
  const [locations, setLocations] = useState([]);
  const [purgeSettlementId, setPurgeSettlementId] = useState('');
  const [purgeLocationId, setPurgeLocationId] = useState('');
  const [purgeModalOpen, setPurgeModalOpen] = useState(false);
  const [purgeLoading, setPurgeLoading] = useState(false);
  const [purgeConfirmLoading, setPurgeConfirmLoading] = useState(false);
  const [purgeIds, setPurgeIds] = useState([]);
  const [purgeFetchError, setPurgeFetchError] = useState('');
  const [purgeAck, setPurgeAck] = useState(false);
  const [showPurgePanel, setShowPurgePanel] = useState(false);
  const [studentLabels, setStudentLabels] = useState({});
  const [labelDefinitions, setLabelDefinitions] = useState([]);
  const [labelsLoadError, setLabelsLoadError] = useState(false);
  const tableBodyRef = useRef(null);
  const exportMenuRef = useRef(null);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);

  const labelFilterIds = useMemo(
    () => parseLabelIdsParam(searchParams.get('labels')),
    [searchParams]
  );
  const labelsParamKey = searchParams.get('labels') || '';

  /** Last values applied by URL sync — used so we never wipe ?page unless filters/date/sort actually changed. */
  const voucherUrlSyncRef = useRef({
    selectedDate: '',
    preset: '',
    searchTerm: '',
    locationFilter: '',
    labelsParam: '',
    sortKey: '',
    sortDir: ''
  });

  useEffect(() => {
    if (!exportMenuOpen) return undefined;
    const onDoc = (e) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target)) {
        setExportMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [exportMenuOpen]);

  useEffect(() => {
    let mounted = true;
    const params = new URLSearchParams(window.location.search);
    Promise.all([getSettlementDatesForWorkspace(), getSettlementDatesWithVouchers()]).then(([dates, withVouchers]) => {
      if (!mounted) return;
      const arr = Array.isArray(dates) ? dates : [];
      const withData = Array.isArray(withVouchers) ? withVouchers : [];
      setSettlementDates(arr);
      const urlDate = params.get('date');
      if (urlDate && arr.some((d) => String(d.id) === String(urlDate))) {
        setSelectedDate(urlDate);
      } else if (arr.length) {
        const defaultId =
          withData.length > 0 ? String(withData[0].id) : String(arr[0].id);
        setSelectedDate((cur) => cur || defaultId);
      }
    });
    getLocations()
      .then((data) => setLocations(Array.isArray(data) ? data : []))
      .catch(() => setLocations([]));
    listLabels('student')
      .then((data) => {
        if (!mounted) return;
        setLabelsLoadError(false);
        setLabelDefinitions(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!mounted) return;
        setLabelsLoadError(true);
        setLabelDefinitions([]);
      });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (selectedDate) setPurgeSettlementId(selectedDate);
  }, [selectedDate]);

  useEffect(() => {
    if (!selectedDate) return;
    setLoading(true);
    getVoucherStatuses(selectedDate)
      .then(setStatusData)
      .finally(() => setLoading(false));
  }, [selectedDate]);

  useEffect(() => {
    async function fetchClosures() {
      if (!selectedDate) return;
      try {
        const statuses = await getAllClosureStatuses(selectedDate);
        setStatusData((data) =>
          data.map((row) =>
            statuses && statuses[row.student_id] ? { ...row, status: statuses[row.student_id] } : row
          )
        );
      } catch {
        /* ignore */
      }
    }
    fetchClosures();
  }, [selectedDate]);

  useEffect(() => {
    if (!selectedDate) return;
    const r = voucherUrlSyncRef.current;
    const resetPage =
      r.selectedDate !== selectedDate ||
      r.preset !== preset ||
      r.searchTerm !== searchTerm ||
      r.locationFilter !== locationFilter ||
      r.labelsParam !== labelsParamKey ||
      r.sortKey !== sortConfig.key ||
      r.sortDir !== sortConfig.direction;

    r.selectedDate = selectedDate;
    r.preset = preset;
    r.searchTerm = searchTerm;
    r.locationFilter = locationFilter;
    r.labelsParam = labelsParamKey;
    r.sortKey = sortConfig.key;
    r.sortDir = sortConfig.direction;

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('date', selectedDate);
        if (preset === 'all') next.delete('view');
        else next.set('view', preset);
        if (searchTerm) next.set('search', searchTerm);
        else next.delete('search');
        if (resetPage) next.delete('page');
        return next;
      },
      { replace: true }
    );
  }, [
    selectedDate,
    preset,
    searchTerm,
    locationFilter,
    labelsParamKey,
    sortConfig.key,
    sortConfig.direction,
    setSearchParams
  ]);

  const rosterRows = useMemo(
    () => statusData.filter((row) => row.active !== false && (row.expected > 0 || row.actual > 0)),
    [statusData]
  );

  useEffect(() => {
    let cancelled = false;
    const needed = new Set(rosterRows.map((r) => studentRowKey(r.student_id)).filter(Boolean));
    if (needed.size === 0) {
      setStudentLabels({});
      return undefined;
    }

    async function loadLabels() {
      const map = {};
      let pg = 1;
      const size = 200;
      while (!cancelled && [...needed].some((id) => map[id] === undefined)) {
        const res = await fetch(`${API_URL}/api/students?page=${pg}&size=${size}&status=all`, {
          headers: authHeaders()
        });
        if (!res.ok) break;
        const body = await res.json();
        const list = body.data || [];
        const total = body.total ?? list.length;
        for (const s of list) {
          const sk = studentRowKey(s.id);
          if (needed.has(sk)) map[sk] = s.labels || [];
        }
        pg += 1;
        if (!list.length || (pg - 1) * size >= total || pg > 80) break;
      }
      if (!cancelled) setStudentLabels(map);
    }
    loadLabels();
    return () => {
      cancelled = true;
    };
  }, [selectedDate, rosterRows]);

  const baseFilteredData = useMemo(() => {
    let data = rosterRows.filter((row) => matchesPreset(row, preset));
    data = data.filter((row) => rowMatchesLabelFilter(row, labelFilterIds, studentLabels));
    data = data.filter((row) => rowMatchesSearch(row, searchTerm, studentLabels));
    return data;
  }, [rosterRows, preset, searchTerm, studentLabels, labelFilterIds]);

  const locationCounts = useMemo(() => {
    const counts = {};
    rosterRows.forEach((row) => {
      let loc = 'No location';
      if (row.location_address) loc = row.location_address;
      if (!matchesPreset(row, preset)) return;
      if (!rowMatchesLabelFilter(row, labelFilterIds, studentLabels)) return;
      if (!rowMatchesSearch(row, searchTerm, studentLabels)) return;
      counts[loc] = (counts[loc] || 0) + 1;
    });
    return counts;
  }, [rosterRows, preset, searchTerm, studentLabels, labelFilterIds]);

  const sidebarStudentLabels = useMemo(
    () =>
      [...labelDefinitions]
        .filter((l) => l.kind === 'student' || l.kind === 'both')
        .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''))),
    [labelDefinitions]
  );

  const toggleVoucherLabelFilter = (id) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        const cur = parseLabelIdsParam(next.get('labels'));
        const set = new Set(cur);
        if (set.has(id)) set.delete(id);
        else set.add(id);
        const arr = [...set].sort((a, b) => a - b);
        if (arr.length === 0) next.delete('labels');
        else next.set('labels', arr.join(','));
        next.delete('page');
        return next;
      },
      { replace: true }
    );
  };

  const clearVoucherLabelFilter = () => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('labels');
        next.delete('page');
        return next;
      },
      { replace: true }
    );
  };

  const labelOptionCounts = useMemo(() => {
    const out = {};
    for (const l of sidebarStudentLabels) out[l.id] = 0;
    for (const row of rosterRows) {
      if (!matchesPreset(row, preset)) continue;
      if (!rowMatchesSearch(row, searchTerm, studentLabels)) continue;
      for (const lab of studentLabels[studentRowKey(row.student_id)] || []) {
        if (Object.prototype.hasOwnProperty.call(out, lab.id)) {
          out[lab.id] += 1;
        }
      }
    }
    return out;
  }, [rosterRows, preset, searchTerm, studentLabels, sidebarStudentLabels]);

  const filteredData = useMemo(() => {
    let data = baseFilteredData;
    if (locationFilter !== 'all') {
      data = data.filter((row) => {
        let loc = 'No location';
        if (row.location_address) loc = row.location_address;
        return loc === locationFilter;
      });
    }
    const gapOf = (row) => Number(row.expected) - Number(row.actual);
    if (sortConfig.key) {
      data = [...data].sort((a, b) => {
        let aVal;
        let bVal;
        switch (sortConfig.key) {
          case 'gap':
            aVal = gapOf(a);
            bVal = gapOf(b);
            break;
          case 'expected':
            aVal = Number(a.expected);
            bVal = Number(b.expected);
            break;
          case 'actual':
            aVal = Number(a.actual);
            bVal = Number(b.actual);
            break;
          case 'days':
            aVal = Number(a.days);
            bVal = Number(b.days);
            break;
          case 'dob':
            aVal = a.dob ? new Date(a.dob).getTime() : 0;
            bVal = b.dob ? new Date(b.dob).getTime() : 0;
            break;
          case 'notes':
            aVal = notesCounts[studentRowKey(a.student_id)] ?? 0;
            bVal = notesCounts[studentRowKey(b.student_id)] ?? 0;
            break;
          case 'labels': {
            const la = (studentLabels[studentRowKey(a.student_id)] || [])
              .map((x) => x.name)
              .sort((x, y) => x.localeCompare(y))
              .join(', ');
            const lb = (studentLabels[studentRowKey(b.student_id)] || [])
              .map((x) => x.name)
              .sort((x, y) => x.localeCompare(y))
              .join(', ');
            aVal = la.toLowerCase();
            bVal = lb.toLowerCase();
            break;
          }
          default:
            aVal = String(a[sortConfig.key] || '').toLowerCase();
            bVal = String(b[sortConfig.key] || '').toLowerCase();
        }
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return data;
  }, [baseFilteredData, locationFilter, sortConfig, notesCounts, studentLabels]);

  const filteredMoneyTotals = useMemo(() => {
    let expected = 0;
    let actual = 0;
    for (const row of filteredData) {
      expected += Number(row.expected) || 0;
      actual += Number(row.actual) || 0;
    }
    return { expected, actual };
  }, [filteredData]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(filteredData.length / pageSize) || 1);
    if (page <= maxPage) return;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('page', String(maxPage));
      return next;
    }, { replace: true });
  }, [filteredData.length, pageSize, page, setSearchParams]);

  useEffect(() => {
    if (pageSizePref.mode !== 'manual') return;
    if (urlPageSize === pageSizePref.size) return;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('size', String(pageSizePref.size));
      return next;
    }, { replace: true });
  }, [pageSizePref, urlPageSize, setSearchParams]);

  const handlePageSizeChange = useCallback((next) => {
    if (next === 'auto') {
      setTablePageSizePreference({ mode: 'auto' });
      return;
    }
    const size = Math.min(PAGE_SIZE_MAX, Math.max(PAGE_SIZE_MIN, Number(next) || PAGE_SIZE_DEFAULT));
    setTablePageSizePreference({ mode: 'manual', size });
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.set('size', String(size));
      p.set('page', '1');
      return p;
    }, { replace: true });
  }, [setSearchParams]);

  useEffect(() => {
    if (pageSizePref.mode === 'manual') return undefined;
    const el = tableBodyRef.current;
    if (!el) return undefined;

    let timer;
    const apply = () => {
      if (loading || filteredData.length === 0) return;
      const next = computeFitRowsForContainer(el);
      if (next == null) return;
      const params = new URLSearchParams(window.location.search);
      const cur = Math.min(
        PAGE_SIZE_MAX,
        Math.max(PAGE_SIZE_MIN, parseInt(params.get('size') || String(PAGE_SIZE_DEFAULT), 10))
      );
      if (next === cur) return;
      setSearchParams((prev) => {
        const p = new URLSearchParams(prev);
        p.set('size', String(next));
        return p;
      }, { replace: true });
    };

    const schedule = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(apply, ROW_MEASURE_DEBOUNCE_MS);
    };

    const ro = new ResizeObserver(schedule);
    ro.observe(el);
    schedule();

    return () => {
      window.clearTimeout(timer);
      ro.disconnect();
    };
  }, [setSearchParams, loading, filteredData.length, pageSizePref.mode]);

  const pageRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredData.slice(start, start + pageSize);
  }, [filteredData, page, pageSize]);

  useEffect(() => {
    async function fetchCounts() {
      try {
        if (!selectedDate) return setNotesCounts({});
        const counts = await getVoucherStatusNotesCounts(selectedDate);
        const norm = {};
        for (const [k, v] of Object.entries(counts || {})) {
          norm[String(k)] = Number(v) || 0;
        }
        setNotesCounts(norm);
      } catch {
        setNotesCounts({});
      }
    }
    fetchCounts();
  }, [selectedDate, filteredData.length]);

  const detailStudent = useMemo(() => {
    if (!detailStudentId) return null;
    return statusData.find((s) => String(s.student_id) === String(detailStudentId));
  }, [detailStudentId, statusData]);

  const handleSort = (key) => {
    setSortConfig((prev) =>
      prev.key === key ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' } : { key, direction: 'asc' }
    );
  };

  async function handleSetClosure(student_ids, status) {
    const ids = Array.isArray(student_ids) ? student_ids : [student_ids];
    setClosureLoading((l) => ({ ...l, ...Object.fromEntries(ids.map((id) => [id, true])) }));
    try {
      await Promise.all(ids.map((id) => setClosureStatus(id, selectedDate, status)));
      if (status === 'closed') {
        setStatusData((data) =>
          data.map((row) => (ids.includes(row.student_id) ? { ...row, status: 'closed' } : row))
        );
      } else if (status === 'reopened') {
        setStatusData((data) =>
          data.map((row) =>
            ids.includes(row.student_id) ? { ...row, status: row.calculatedStatus || 'missing' } : row
          )
        );
      }
      showPopup(status === 'closed' ? 'Marked closed' : 'Reopened', 'success');
    } catch (e) {
      showPopup(e?.message || 'Update failed', 'error');
    } finally {
      setClosureLoading((l) => ({ ...l, ...Object.fromEntries(ids.map((id) => [id, false])) }));
    }
  }

  const exportPeriodVoucherDate = useMemo(() => {
    const d = settlementDates.find((x) => String(x.id) === String(selectedDate));
    if (!d?.settlement_date) return '';
    return formatEnUsNumericDate(d.settlement_date);
  }, [settlementDates, selectedDate]);

  const runExportImportShape = useCallback(async () => {
    if (!selectedDate) {
      showPopup('Choose a settlement period first.', 'error');
      return;
    }
    try {
      const raw = await getVouchers(selectedDate);
      if (!Array.isArray(raw)) {
        showPopup(typeof raw?.message === 'string' ? raw.message : 'Could not load vouchers for export.', 'error');
        return;
      }
      const list = filterVoucherApiListForWorkspace(raw, locations, locationFilter, filteredData);
      const rows = list.map((v) => voucherToImportExportRow(v));
      if (filteredData.length > 0) {
        const covered = studentPksCoveredByVoucherList(list, filteredData);
        for (const row of filteredData) {
          if (!covered.has(row.student_id != null ? String(row.student_id) : '')) {
            rows.push(emptyImportShapeRowFromRoster(row));
          }
        }
      }
      if (!rows.length) {
        showPopup('No rows to export for this period and filters.', 'error');
        return;
      }
      const buf = buildImportShapeExportBuffer(rows);
      saveAs(new Blob([buf], { type: 'application/octet-stream' }), 'voucher-export.xlsx');
      showPopup('Exported (same columns as import)', 'success');
    } catch (e) {
      showPopup(e?.response?.data?.message || e?.message || 'Export failed', 'error');
    }
  }, [selectedDate, locations, locationFilter, filteredData, showPopup]);

  const runExportSimpleColumns = useCallback(async () => {
    if (!selectedDate) {
      showPopup('Choose a settlement period first.', 'error');
      return;
    }
    try {
      const raw = await getVouchers(selectedDate);
      if (!Array.isArray(raw)) {
        showPopup(typeof raw?.message === 'string' ? raw.message : 'Could not load vouchers for export.', 'error');
        return;
      }
      const list = filterVoucherApiListForWorkspace(raw, locations, locationFilter, filteredData);
      const rows = [];
      for (const v of list) {
        rows.push(voucherApiToSimpleExportRow(v, exportPeriodVoucherDate));
      }
      if (filteredData.length > 0) {
        const covered = studentPksCoveredByVoucherList(list, filteredData);
        for (const row of filteredData) {
          if (!covered.has(row.student_id != null ? String(row.student_id) : '')) {
            rows.push({
              'Child Name': childNameForSimpleExport(row.name, null),
              VoucherDate: exportPeriodVoucherDate,
              Received: formatReceivedDollarExport(0)
            });
          }
        }
      }
      if (!rows.length) {
        showPopup('No rows to export for this period and filters.', 'error');
        return;
      }
      const buf = buildSimpleVoucherExportBuffer(rows);
      saveAs(new Blob([buf], { type: 'application/octet-stream' }), 'voucher-export-simple.xlsx');
      showPopup('Exported (Child Name, VoucherDate, Received)', 'success');
    } catch (e) {
      showPopup(e?.response?.data?.message || e?.message || 'Export failed', 'error');
    }
  }, [selectedDate, locations, locationFilter, filteredData, exportPeriodVoucherDate, showPopup]);

  const resetFilters = () => {
    setPreset('all');
    setLocationFilter('all');
    setSearchTerm('');
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('labels');
      next.delete('page');
      return next;
    }, { replace: true });
  };

  const handlePresetChange = useCallback((nextPreset) => {
    setPreset(nextPreset);
    setLocationFilter('all');
  }, []);

  const settlementLabelForImport = useMemo(() => {
    const d = settlementDates.find((x) => String(x.id) === String(selectedDate));
    if (!d?.settlement_date) return selectedDate ? `ID ${selectedDate}` : '';
    const [datePart] = String(d.settlement_date).split('T');
    const [y, m, day] = datePart.split('-');
    const dt = new Date(Number(y), Number(m) - 1, Number(day));
    return Number.isNaN(dt.getTime())
      ? String(d.date_range || selectedDate)
      : dt.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  }, [settlementDates, selectedDate]);

  const purgeSettlementMeta = useMemo(
    () => settlementDates.find((x) => String(x.id) === String(purgeSettlementId)),
    [settlementDates, purgeSettlementId]
  );

  const purgeLocationName = useMemo(() => {
    if (!purgeLocationId) return null;
    const loc = locations.find((l) => String(l.id) === String(purgeLocationId));
    return loc?.name || null;
  }, [locations, purgeLocationId]);

  const openPurgePreview = async () => {
    if (!purgeSettlementId) {
      showPopup('Choose a settlement period first.', 'error');
      return;
    }
    setPurgeModalOpen(true);
    setPurgeAck(false);
    setPurgeFetchError('');
    setPurgeLoading(true);
    setPurgeIds([]);
    try {
      const rows = await getVouchers(purgeSettlementId);
      const list = Array.isArray(rows) ? rows : [];
      const filtered = purgeLocationId
        ? list.filter((v) => String(v.location_id) === String(purgeLocationId))
        : list;
      const ids = filtered.map((v) => v.id).filter((id) => id != null);
      setPurgeIds(ids);
    } catch (e) {
      setPurgeFetchError(e?.response?.data?.message || e?.message || 'Failed to load vouchers');
      setPurgeIds([]);
    } finally {
      setPurgeLoading(false);
    }
  };

  const closePurgeModal = () => {
    if (purgeConfirmLoading) return;
    setPurgeModalOpen(false);
    setPurgeAck(false);
    setPurgeFetchError('');
    setPurgeIds([]);
  };

  const confirmPurge = async () => {
    if (!purgeIds.length || !purgeAck) return;
    const removedCount = purgeIds.length;
    setPurgeConfirmLoading(true);
    try {
      await deleteVouchersInBatches(purgeIds);
      setPurgeModalOpen(false);
      setShowPurgePanel(false);
      setPurgeAck(false);
      setPurgeFetchError('');
      setPurgeIds([]);
      const dates = await getSettlementDatesForWorkspace();
      const arr = Array.isArray(dates) ? dates : [];
      setSettlementDates(arr);
      if (selectedDate) {
        const fresh = await getVoucherStatuses(selectedDate);
        setStatusData(fresh);
      }
      try {
        if (selectedDate) {
          const counts = await getVoucherStatusNotesCounts(selectedDate);
          setNotesCounts(counts);
        } else {
          setNotesCounts({});
        }
      } catch {
        setNotesCounts({});
      }
      showPopup(
        `Removed ${removedCount} voucher row${removedCount === 1 ? '' : 's'}.`,
        'success'
      );
    } catch (e) {
      showPopup(e?.response?.data?.message || e?.message || 'Delete failed', 'error');
    } finally {
      setPurgeConfirmLoading(false);
    }
  };

  const refreshAfterImport = async () => {
    const dates = await getSettlementDatesForWorkspace();
    const arr = Array.isArray(dates) ? dates : [];
    setSettlementDates(arr);
    if (selectedDate) {
      const fresh = await getVoucherStatuses(selectedDate);
      setStatusData(fresh);
    }
    showPopup('Vouchers imported', 'success');
  };

  return (
    <>
      <div className={dirStyles.pageRoot}>
        <div className={dirStyles.layout}>
          <VouchersWorkspaceSidebar
            settlementDates={settlementDates}
            selectedDate={selectedDate}
            onDateChange={(id) => {
              setSelectedDate(id);
              setDetailStudentId(null);
            }}
            preset={preset}
            onPresetChange={handlePresetChange}
            rosterRows={rosterRows}
            searchTerm={searchTerm}
            studentLabels={studentLabels}
            sidebarLabels={sidebarStudentLabels}
            labelFilterIds={labelFilterIds}
            labelOptionCounts={labelOptionCounts}
            labelsLoadError={labelsLoadError}
            onToggleLabelFilter={toggleVoucherLabelFilter}
            onClearLabelFilter={clearVoucherLabelFilter}
            locationFilter={locationFilter}
            locationCounts={locationCounts}
            onLocationChange={setLocationFilter}
            onResetFilters={resetFilters}
          />

          <div className={dirStyles.main}>
            <div className={styles.toolbar}>
              <div className={styles.toolbarMain}>
                <div className={styles.search}>
                  <Input
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search student, family, or label"
                    iconLeft={<FaSearch />}
                  />
                </div>
                <div className={styles.toolbarActions}>
                  <div className={styles.exportWrap} ref={exportMenuRef}>
                    <Button
                      type="button"
                      variant="secondary"
                      iconLeft={<FaDownload />}
                      iconRight={<FaChevronDown />}
                      aria-expanded={exportMenuOpen}
                      aria-haspopup="menu"
                      onClick={() => setExportMenuOpen((o) => !o)}
                    >
                      Export
                    </Button>
                    {exportMenuOpen ? (
                      <div className={styles.exportMenu} role="menu">
                        <button
                          type="button"
                          role="menuitem"
                          className={styles.exportMenuItem}
                          onClick={() => {
                            runExportImportShape();
                            setExportMenuOpen(false);
                          }}
                        >
                          Export (import columns)
                        </button>
                        <button
                          type="button"
                          role="menuitem"
                          className={styles.exportMenuItem}
                          onClick={() => {
                            runExportSimpleColumns();
                            setExportMenuOpen(false);
                          }}
                        >
                          Export (Child Name, VoucherDate, Received)
                        </button>
                      </div>
                    ) : null}
                  </div>
                  <Button variant="secondary" iconLeft={<FaPlus />} onClick={() => setImportOpen(true)}>
                    Import voucher rows
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    iconLeft={<FaTrash />}
                    aria-expanded={showPurgePanel}
                    aria-controls="vouchers-purge-panel"
                    onClick={() => setShowPurgePanel((v) => !v)}
                  >
                    {showPurgePanel ? 'Hide remove rows' : 'Remove imported rows'}
                  </Button>
                </div>
              </div>

              {labelFilterIds.length > 0 ? (
                <div className={dirStyles.filterPills}>
                  <span className={dirStyles.filterPillsLabel}>Labels:</span>
                  {sidebarStudentLabels
                    .filter((l) => labelFilterIds.includes(l.id))
                    .map((l) => (
                      <LabelChip
                        key={l.id}
                        label={l.name}
                        color={l.color}
                        onRemove={() => toggleVoucherLabelFilter(l.id)}
                      />
                    ))}
                  <button type="button" className={dirStyles.clearFilters} onClick={clearVoucherLabelFilter}>
                    Clear labels
                  </button>
                </div>
              ) : null}

              {showPurgePanel ? (
                <div id="vouchers-purge-panel" className={styles.scopePurgeBar}>
                  <p className={styles.scopePurgeHint}>
                    <strong>Remove imported rows</strong> — use when data landed under the wrong settlement period or
                    location. This deletes raw voucher lines only (not students or closure notes).
                  </p>
                  <div className={styles.scopePurgeFields}>
                    <div className={styles.scopePurgeField}>
                      <span className={styles.scopePurgeLabel} id="purge-settlement-lbl">
                        Settlement period
                      </span>
                      <select
                        id="purge-settlement"
                        className={styles.scopePurgeSelect}
                        aria-labelledby="purge-settlement-lbl"
                        value={purgeSettlementId}
                        onChange={(e) => setPurgeSettlementId(e.target.value)}
                        disabled={settlementDates.length === 0}
                      >
                        {settlementDates.length === 0 ? (
                          <option value="">No periods with vouchers</option>
                        ) : (
                          settlementDates.map((d) => (
                            <option key={d.id} value={d.id}>
                              {settlementPeriodLabel(d)}
                            </option>
                          ))
                        )}
                      </select>
                    </div>
                    <div className={styles.scopePurgeField}>
                      <span className={styles.scopePurgeLabel} id="purge-location-lbl">
                        Location
                      </span>
                      <select
                        id="purge-location"
                        className={styles.scopePurgeSelect}
                        aria-labelledby="purge-location-lbl"
                        value={purgeLocationId}
                        onChange={(e) => setPurgeLocationId(e.target.value)}
                        disabled={locations.length === 0}
                      >
                        <option value="">All locations</option>
                        {locations.map((l) => (
                          <option key={l.id} value={l.id}>
                            {l.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className={styles.scopePurgeActions}>
                      <Button
                        type="button"
                        variant="danger"
                        iconLeft={<FaTrash />}
                        disabled={!purgeSettlementId}
                        onClick={openPurgePreview}
                      >
                        Delete voucher rows…
                      </Button>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>

            <Card className={styles.tableCard}>
              <CardBody ref={tableBodyRef} className={styles.tableBody}>
                {loading ? (
                  <div className={styles.skeleton}>
                    {[0, 1, 2, 3, 4].map((i) => (
                      <div key={i} className={styles.skeletonRow} style={{ animationDelay: `${i * 70}ms` }} />
                    ))}
                  </div>
                ) : filteredData.length === 0 ? (
                  <div className={styles.empty}>
                    <h3>Nothing in this queue.</h3>
                    <p>
                      Try another preset or label filter, widen search, or import voucher rows for this settlement
                      period if you have not yet.
                    </p>
                  </div>
                ) : (
                  <div className={styles.tableWrap}>
                    <table className={styles.table}>
                      <thead>
                        <tr>
                          <th onClick={() => handleSort('name')} style={{ cursor: 'pointer' }}>
                            Student {sortConfig.key === 'name' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : ''}
                          </th>
                          <th onClick={() => handleSort('labels')} style={{ cursor: 'pointer' }}>
                            Labels{' '}
                            {sortConfig.key === 'labels' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : ''}
                          </th>
                          <th onClick={() => handleSort('family_name')} style={{ cursor: 'pointer' }}>
                            Family{' '}
                            {sortConfig.key === 'family_name' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : ''}
                          </th>
                          <th onClick={() => handleSort('dob')} style={{ cursor: 'pointer' }}>
                            DOB {sortConfig.key === 'dob' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : ''}
                          </th>
                          <th className={styles.num} onClick={() => handleSort('days')} style={{ cursor: 'pointer' }}>
                            Days {sortConfig.key === 'days' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : ''}
                          </th>
                          <th className={styles.num} onClick={() => handleSort('expected')} style={{ cursor: 'pointer' }}>
                            Expected{' '}
                            {sortConfig.key === 'expected' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : ''}
                          </th>
                          <th className={styles.num} onClick={() => handleSort('actual')} style={{ cursor: 'pointer' }}>
                            Actual {sortConfig.key === 'actual' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : ''}
                          </th>
                          <th className={styles.num} onClick={() => handleSort('gap')} style={{ cursor: 'pointer' }}>
                            Gap {sortConfig.key === 'gap' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : ''}
                          </th>
                          <th onClick={() => handleSort('status')} style={{ cursor: 'pointer' }}>
                            Status {sortConfig.key === 'status' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : ''}
                          </th>
                          <th className={styles.num} onClick={() => handleSort('notes')} style={{ cursor: 'pointer' }}>
                            Notes {sortConfig.key === 'notes' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : ''}
                          </th>
                          <th className={styles.num}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pageRows.map((row) => {
                          const gap = Number(row.expected) - Number(row.actual);
                          const gapCls = gap > 0 ? styles.gapNeg : gap < 0 ? styles.gapPos : '';
                          let loc = 'No location';
                          if (row.location_address) loc = row.location_address;
                          const highlight = locationFilter !== 'all' && loc === locationFilter;
                          const lbls = studentLabels[studentRowKey(row.student_id)] || [];
                          return (
                            <tr
                              key={row.student_id}
                              style={
                                highlight ? { boxShadow: 'inset 3px 0 0 var(--c-brand)' } : undefined
                              }
                            >
                              <td>
                                <button
                                  type="button"
                                  className={[dirStyles.entityBtn, styles.studentOpenBtn].join(' ')}
                                  onClick={() => setDetailStudentId(row.student_id)}
                                  aria-label={`Open details for ${row.name}`}
                                >
                                  <span className={dirStyles.entityName}>{row.name}</span>
                                  <span className={dirStyles.entityMeta}>{loc}</span>
                                </button>
                              </td>
                              <td>
                                <div className={styles.labelCell}>
                                  {lbls.length === 0 ? (
                                    <span className={styles.muted}>—</span>
                                  ) : (
                                    lbls.map((l) => (
                                      <LabelChip
                                        key={l.id}
                                        label={l.name}
                                        color={l.color || 'neutral'}
                                        className={styles.tableLabelChip}
                                      />
                                    ))
                                  )}
                                </div>
                              </td>
                              <td>{row.family_name || <span className={styles.muted}>—</span>}</td>
                              <td>{row.dob ? new Date(row.dob).toLocaleDateString() : '—'}</td>
                              <td className={styles.num}>{row.days}</td>
                              <td className={styles.num}>{formatMoney(row.expected)}</td>
                              <td className={styles.num}>{formatMoney(row.actual)}</td>
                              <td className={`${styles.num} ${gapCls}`}>{formatMoney(gap)}</td>
                              <td className={styles.statusCell}>
                                <Badge variant={badgeVariant(row.status)}>
                                  {row.status.charAt(0).toUpperCase() + row.status.slice(1)}
                                </Badge>
                              </td>
                              <td className={`${styles.num} ${styles.notesCell}`}>
                                <span className={styles.notesCount}>
                                  {notesCounts[studentRowKey(row.student_id)] ?? 0}
                                </span>
                              </td>
                              <td className={styles.num}>
                                <div className={styles.rowActions}>
                                  {closureLoading[row.student_id] ? (
                                    <span className={styles.muted}>…</span>
                                  ) : row.status === 'closed' ? (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className={styles.tableRowActionBtn}
                                      iconLeft={<FaUndo />}
                                      onClick={() => handleSetClosure(row.student_id, 'reopened')}
                                    >
                                      Reopen
                                    </Button>
                                  ) : row.status === 'missing' || row.status === 'partial' ? (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className={styles.tableRowActionBtn}
                                      iconLeft={<FaCheckCircle />}
                                      onClick={() => handleSetClosure(row.student_id, 'closed')}
                                    >
                                      Close
                                    </Button>
                                  ) : null}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardBody>
            </Card>

            {filteredData.length > 0 ? (
              <div className={dirStyles.footRow}>
                <div className={`${dirStyles.pagerCenter} ${styles.pagerFootRow}`}>
                  <Pagination
                    page={page}
                    pageSize={pageSize}
                    sizeMode={pageSizePref.mode}
                    total={filteredData.length}
                    onPageChange={(n) => {
                      setSearchParams((prev) => {
                        const next = new URLSearchParams(prev);
                        next.set('page', String(n));
                        return next;
                      }, { replace: true });
                    }}
                    onPageSizeChange={handlePageSizeChange}
                  />
                  <p className={styles.pagerMoneyTotals} aria-live="polite">
                    <span className={styles.pagerMoneyTotalsLabel}>Filtered:</span>
                    <span title="Sum of expected for rows matching current filters">
                      Expected <strong>{formatMoney(filteredMoneyTotals.expected)}</strong>
                    </span>
                    <span className={styles.pagerMoneyTotalsSep} aria-hidden>
                      ·
                    </span>
                    <span title="Sum of received (actual) for rows matching current filters">
                      Received <strong>{formatMoney(filteredMoneyTotals.actual)}</strong>
                    </span>
                  </p>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <Modal
        open={purgeModalOpen}
        onClose={closePurgeModal}
        title="Delete voucher rows"
        description="Permanently removes imported voucher lines from the database."
        size="md"
        closeOnBackdrop={!purgeConfirmLoading}
        footer={
          <div className={styles.purgeModalFooter}>
            <Button
              variant="secondary"
              type="button"
              onClick={closePurgeModal}
              disabled={purgeConfirmLoading}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              type="button"
              loading={purgeConfirmLoading}
              disabled={
                purgeLoading ||
                Boolean(purgeFetchError) ||
                purgeIds.length === 0 ||
                !purgeAck ||
                purgeConfirmLoading
              }
              onClick={confirmPurge}
            >
              Delete permanently
            </Button>
          </div>
        }
      >
        <div className={styles.purgeModalBody}>
          {purgeLoading ? <p className={styles.muted}>Loading voucher rows…</p> : null}
          {purgeFetchError ? <p className={styles.purgeModalError}>{purgeFetchError}</p> : null}
          {!purgeLoading && !purgeFetchError && purgeIds.length === 0 ? (
            <p className={styles.purgeModalCount}>
              No voucher rows match this settlement period and location.
            </p>
          ) : null}
          {!purgeLoading && !purgeFetchError && purgeIds.length > 0 ? (
            <>
              <p className={styles.purgeModalCount}>
                {purgeIds.length} voucher row{purgeIds.length === 1 ? '' : 's'} will be deleted.
              </p>
              <ul className={styles.purgeModalList}>
                <li>
                  Settlement:{' '}
                  <strong>
                    {settlementPeriodLabel(purgeSettlementMeta) || `Period #${purgeSettlementId}`}
                  </strong>
                </li>
                <li>
                  Location:{' '}
                  <strong>
                    {purgeLocationId
                      ? purgeLocationName || 'Selected location'
                      : 'All locations'}
                  </strong>
                </li>
              </ul>
              <label className={styles.purgeModalAck}>
                <input
                  type="checkbox"
                  checked={purgeAck}
                  onChange={(e) => setPurgeAck(e.target.checked)}
                  disabled={purgeConfirmLoading}
                />
                <span>I understand these rows cannot be recovered from the app.</span>
              </label>
            </>
          ) : null}
        </div>
      </Modal>

      <VoucherImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        settlementDateId={selectedDate}
        settlementLabel={settlementLabelForImport}
        locations={locations}
        onSuccess={refreshAfterImport}
      />

      <VoucherStudentWorkspaceModal
        open={!!detailStudent}
        onClose={() => setDetailStudentId(null)}
        student={detailStudent}
        settlementDateId={selectedDate}
        notesCount={
          detailStudent ? notesCounts[studentRowKey(detailStudent.student_id)] ?? 0 : 0
        }
        closureLoading={detailStudent ? !!closureLoading[detailStudent.student_id] : false}
        onMarkClosed={() => detailStudent && handleSetClosure(detailStudent.student_id, 'closed')}
        onReopen={() => detailStudent && handleSetClosure(detailStudent.student_id, 'reopened')}
        onNotesChange={(n) =>
          detailStudent &&
          setNotesCounts((c) => ({
            ...c,
            [studentRowKey(detailStudent.student_id)]: n
          }))
        }
      />
    </>
  );
}
